import type { ComponentProps } from 'react';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { TYPE_CONFIG, theme } from '@garnish/constants';

import { IconLink } from '../../../Icon';
import { DisplayText } from '../../../Text';
import type { PaletteProps } from '../../Modal.types';

export const ModalHeader = (props: ModalHeaderProps) => {
  const { children, palette, accessibilityLabel, onClose, ...restProps } =
    props;

  const paletteSpecificTextStyles = palette
    ? paletteHeadingTextStyles[palette]
    : undefined;
  const textStyles = [styles.fontSizeDisplay32, paletteSpecificTextStyles];
  const insetStyle = useTopInsetStyle();

  return (
    <View style={[styles.header, insetStyle]}>
      <DisplayText style={textStyles} {...restProps}>
        {children}
      </DisplayText>

      <IconLink
        name="IconClose"
        width={24}
        height={24}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={styles.closeButton}
        onPress={onClose}
      />
    </View>
  );
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Android is missing the top inset spacing for the Modal headers.
 */
function useTopInsetStyle() {
  const { top: topInset } = useSafeAreaInsets();
  const insetStyle = useStyle(
    () => ({ paddingTop: headerPadding + topInset }),
    [headerPadding, topInset],
  );

  return Platform.OS === 'android' ? insetStyle : null;
}

// ─── Styles ────────────────────────────────────────────────────────────

const headerPadding = theme.spacing['6'];
const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: headerPadding,
  },
  headlineSecondary: {
    color: theme.colors.WHITE,
  },
  fontSizeDisplay32: {
    ...TYPE_CONFIG.DISPLAY['32'],
  },
  closeButton: {
    justifyContent: 'flex-start',
  },
});

// ─── Palette Styles ────────────────────────────────────────────────────

const paletteHeadingTextStyles = {
  primary: undefined,
  neutral: undefined,
  secondary: styles.headlineSecondary,
  lime: styles.headlineSecondary,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ModalHeaderProps = ComponentProps<typeof DisplayText> &
  PaletteProps &
  Readonly<{ onClose: (event?: unknown) => void }>;
